import { isAfter, isBefore, isSameDay } from 'date-fns';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task, TaskCheckOptions } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

import { getScheduleByDate } from './schedules';

type Options = {
  day: Timestamp;
  today: Timestamp;
  showRepeatingAsTodo?: boolean;
  weekStartsOn: WeekDays;
};

export const getTaskCheckOption = (
  task: Task,
  { day, today, showRepeatingAsTodo, weekStartsOn }: Options,
) => {
  if (task.type === TaskType.Task) {
    return task.completedAt
      ? TaskCheckOptions.Checked
      : TaskCheckOptions.Unchecked;
  }

  const scheduleForDate = getScheduleByDate(task.schedules, {
    date: day,
    today,
  });

  if (task.completedAt || !scheduleForDate) {
    return TaskCheckOptions.RepeatChecked;
  }

  if (!isSameDay(day, today) && isAfter(day, today)) {
    return TaskCheckOptions.RepeatChecked;
  }

  const dateEntry = scheduleForDate.entries.find(({ date }) =>
    isSameDay(date, day),
  );

  if (dateEntry?.state === ScheduleEntryState.Failure) {
    return TaskCheckOptions.FailedChecked;
  }

  if (dateEntry?.state === ScheduleEntryState.Skip) {
    return TaskCheckOptions.SkippedChecked;
  }

  if (scheduleForDate.frequency) {
    if (
      dateEntry?.state === ScheduleEntryState.Complete &&
      (dateEntry?.value ?? 0) >= scheduleForDate.completionTarget.count
    ) {
      return showRepeatingAsTodo
        ? TaskCheckOptions.Checked
        : TaskCheckOptions.RepeatChecked;
    }

    return TaskCheckOptions.Unchecked;
  }

  if (
    scheduleForDate.entries.find(
      ({ state, date, value }) =>
        isSameDay(date, day) &&
        state === ScheduleEntryState.Complete &&
        (value ?? 0) >= scheduleForDate.completionTarget.count,
    )
  ) {
    return showRepeatingAsTodo
      ? TaskCheckOptions.Checked
      : TaskCheckOptions.RepeatSingleChecked;
  }

  // // when the task is not completed and the day is before today, it is not checked
  if (isBefore(day, today) && !isSameDay(day, today)) {
    return TaskCheckOptions.Unchecked;
  }

  const planning = getDueTaskPlanning(task, {
    date: day,
    today,
    weekStartsOn,
    uncompletedOnly: true,
  });
  const dueDate = planning[0]?.date;

  if (!dueDate || (!isSameDay(dueDate, day) && isAfter(dueDate, day))) {
    return TaskCheckOptions.RepeatSingleChecked;
  }

  return TaskCheckOptions.Unchecked;
};
